import type { RoutePlanForm, RoutePlanVisit } from '@/api/route-plans';
import type { PatientAddress } from '@/api/patients';
import type { ExpectedVisit } from '@/api/planning/route-plan-expected-visits';

export interface VisitRow {
  patientId: number | undefined;
  patientName: string;
  patientAddressId: number | null;
  durationMinutes: number;
  timeWindowStart: string;
  timeWindowEnd: string;
  fixedStartTime: string;
  useFixedTime: boolean;
  assignedCaregiverId: number | null;
  skillId: number | null;
  skillName: string | null;
  prioritized: boolean;
}

export interface SetupFormValues {
  date?: string;
  career?: string;
  groupId?: number | null;
  caregiverIds?: number[];
  visits?: VisitRow[];
  fixedStartOffsetMinutes?: number | null;
}

export interface CaregiverHours {
  availableFrom: string;
  availableTo: string;
  lunchDurationMinutes: number;
}

export type CaregiverHoursMap = Record<number, CaregiverHours>;

// Mirrors the backend DB default on route_plan_caregivers.lunch_duration_minutes.
export const DEFAULT_LUNCH_DURATION_MINUTES = 60;
const SHORT_LUNCH_DURATION_MINUTES = 30;
export const LUNCH_DURATION_OPTIONS =
  [SHORT_LUNCH_DURATION_MINUTES, DEFAULT_LUNCH_DURATION_MINUTES] as const;

// A caregiver's availability window is valid when both edges are set and the start is
// strictly before the end. Times are "HH:MM", so lexicographic comparison is correct.
export function caregiverHoursValid(hours: CaregiverHours): boolean {
  return !!hours.availableFrom && !!hours.availableTo && hours.availableFrom < hours.availableTo;
}

const MINUTES_PER_HOUR = 60;

function hmToMinutesOfDay(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);

  return (Number.isFinite(hours) ? hours! : 0) * MINUTES_PER_HOUR +
    (Number.isFinite(minutes) ? minutes! : 0);
}

// "Hasta" is the deadline for the visit to END, so the window must hold the full duration
// (an exact fit is valid: the visit starts right at the window start). Blank edges or a
// non-numeric duration return true — the required/duration validations own those cases.
export function windowFitsDuration(start: string, end: string, durationMinutes: number): boolean {
  if (!start || !end || !Number.isFinite(durationMinutes)) return true;

  return hmToMinutesOfDay(end) - hmToMinutesOfDay(start) >= durationMinutes;
}

const DEFAULT_DURATION_MINUTES = 60;

export type VisitWindow = { start: string; end: string };

// Placeholder window for any row lacking an explicit one (new, prefilled, or a persisted
// fixed-time visit whose window is unused). Callers wired to a company pass its configured
// window instead; this constant is only the fallback. Mixing defaults within one form is
// harmless for dedup: sameVisitTiming compares the ACTIVE dimension only.
const DEFAULT_VISIT_WINDOW: VisitWindow = { start: '08:00', end: '20:00' };

// Spread into buildVisitRow overrides to seed a company-configured default window.
export function windowOverrides(window: VisitWindow): Partial<VisitRow> {
  return { timeWindowStart: window.start, timeWindowEnd: window.end };
}

export function buildVisitRow(
  patientId: number,
  patientName: string,
  patientAddressId: number | null,
  overrides: Partial<VisitRow> = {},
): VisitRow {
  return {
    patientId,
    patientName,
    patientAddressId,
    durationMinutes: DEFAULT_DURATION_MINUTES,
    timeWindowStart: DEFAULT_VISIT_WINDOW.start,
    timeWindowEnd: DEFAULT_VISIT_WINDOW.end,
    fixedStartTime: '',
    useFixedTime: false,
    assignedCaregiverId: null,
    skillId: null,
    skillName: null,
    prioritized: false,
    ...overrides,
  };
}

// A windowed plan pre-fills its range; without a fixed time or window, buildVisitRow keeps
// the default window.
function expectedWindowOverride(ev: ExpectedVisit, useFixedTime: boolean): Partial<VisitRow> {
  if (useFixedTime || !ev.timeWindowStart || !ev.timeWindowEnd) return {};

  return { timeWindowStart: ev.timeWindowStart, timeWindowEnd: ev.timeWindowEnd };
}

export function visitRowFromExpectedVisit(
  ev: ExpectedVisit,
  patientAddressId: number | null,
  defaultWindow: VisitWindow = DEFAULT_VISIT_WINDOW,
): VisitRow {
  const useFixedTime = !!ev.fixedStartTime;

  return buildVisitRow(ev.patientId, ev.patientFullName, patientAddressId, {
    durationMinutes: ev.durationMinutes,
    fixedStartTime: ev.fixedStartTime ?? '',
    useFixedTime,
    assignedCaregiverId: ev.assignedCaregiverId ?? null,
    skillId: ev.skillId ?? null,
    skillName: ev.skillName ?? null,
    ...windowOverrides(defaultWindow),
    ...expectedWindowOverride(ev, useFixedTime),
  });
}

// Two rows for the same patient are duplicates only when their ACTIVE timing matches — so a
// fixed-time plan and two distinct windowed plans each keep their own row. Only the active
// dimension is compared: the inactive window/fixed fields hold placeholder defaults that differ
// between builders, so comparing them would report false differences (e.g. duplicate fixed-time
// rows when reloading expected visits onto a plan that already has them).
export function sameVisitTiming(a: VisitRow, b: VisitRow): boolean {
  if (a.useFixedTime !== b.useFixedTime) return false;

  return a.useFixedTime ?
    a.fixedStartTime === b.fixedStartTime :
    a.timeWindowStart === b.timeWindowStart && a.timeWindowEnd === b.timeWindowEnd;
}

// Expected visits not yet represented in the form: timing-only identity (address is
// irrelevant), deduped both against the existing rows AND within the batch itself.
export function newExpectedVisits(
  expected: ExpectedVisit[],
  rows: VisitRow[],
  defaultWindow: VisitWindow = DEFAULT_VISIT_WINDOW,
): ExpectedVisit[] {
  const accepted = [...rows];
  const fresh: ExpectedVisit[] = [];
  expected.forEach((ev) => {
    const candidate = visitRowFromExpectedVisit(ev, null, defaultWindow);
    const duplicate = accepted.some(
      (row) => row.patientId === candidate.patientId && sameVisitTiming(row, candidate),
    );
    if (duplicate) return;
    accepted.push(candidate);
    fresh.push(ev);
  });

  return fresh;
}

function windowEdge(value: string | null, fallback: string): string {
  return value || fallback;
}

export function visitRowFromPlanVisit(
  visit: RoutePlanVisit,
  defaultWindow: VisitWindow = DEFAULT_VISIT_WINDOW,
): VisitRow {
  return {
    patientId: visit.patientId,
    patientName: visit.patientFullName,
    patientAddressId: visit.patientAddressId ?? null,
    durationMinutes: visit.durationMinutes,
    timeWindowStart: windowEdge(visit.timeWindowStart, defaultWindow.start),
    timeWindowEnd: windowEdge(visit.timeWindowEnd, defaultWindow.end),
    fixedStartTime: visit.fixedStartTime || '',
    useFixedTime: !!visit.fixedStartTime,
    assignedCaregiverId: visit.assignedCaregiverId || null,
    skillId: visit.skillId,
    skillName: visit.skillName,
    // Must round-trip: the update endpoint destroys and recreates every visit, so
    // dropping this here would silently wipe the flag on any re-save of the wizard.
    prioritized: visit.prioritized,
  };
}

export function visitToAttributes(visit: VisitRow) {
  // Fixed-time and window are mutually exclusive; omit the unused pair (undefined
  // keys drop out of the JSON payload).
  const timing = visit.useFixedTime ?
    { fixedStartTime: visit.fixedStartTime } :
    { timeWindowStart: visit.timeWindowStart, timeWindowEnd: visit.timeWindowEnd };

  return {
    patientId: visit.patientId!,
    patientAddressId: visit.patientAddressId ?? null,
    durationMinutes: visit.durationMinutes,
    assignedCaregiverId: visit.assignedCaregiverId || null,
    skillId: visit.skillId || null,
    prioritized: visit.prioritized,
    ...timing,
  };
}

const SECONDS_PER_MINUTE = 60;

// Per-plan fixed-start tolerance, in seconds. The field is prefilled with the company
// default (in minutes), so we only persist an override when it differs; an unchanged or
// blank value sends null and the plan keeps inheriting the company value. Resetting a
// previously overridden plan back to the default likewise sends null, clearing it.
// By design a plan can't "pin" the current default: a value equal to the company default
// is stored as inherit, so later company-default changes propagate to that plan.
export function fixedStartOffsetSeconds(
  minutes: number | null | undefined,
  defaultMinutes: number | null | undefined,
): number | null {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return null;

  const defaultValue = Number(defaultMinutes);
  if (Number.isFinite(defaultValue) && value === defaultValue) return null;

  return Math.round(value * SECONDS_PER_MINUTE);
}

export function buildRoutePlanFormData(
  values: SetupFormValues,
  caregiverHours: CaregiverHoursMap = {},
  defaultOffsetMinutes?: number | null,
): Partial<RoutePlanForm> {
  return {
    date: values.date,
    career: values.career!,
    groupId: values.groupId ?? null,
    optimizerFixedStartOffset: fixedStartOffsetSeconds(values.fixedStartOffsetMinutes, defaultOffsetMinutes),
    routePlanCaregiversAttributes: (values.caregiverIds || []).map((id: number) => {
      const hours = caregiverHours[id];

      // Omit the window keys when no hours are tracked so the backend keeps applying
      // the company default (RoutePlanCaregiver#set_default_availability). The lunch
      // duration must always ride along: the update endpoint destroys and recreates
      // every caregiver row, so an omitted duration silently resets to the default.
      return hours ?
        {
          caregiverId: id,
          availableFrom: hours.availableFrom,
          availableTo: hours.availableTo,
          lunchDurationMinutes: hours.lunchDurationMinutes,
        } :
        { caregiverId: id };
    }),
    routePlanVisitsAttributes: (values.visits || [])
      .filter((visit: VisitRow) => visit.patientId)
      .map(visitToAttributes),
  };
}

function primaryOrFirstAddress(addresses: PatientAddress[]): PatientAddress | undefined {
  return addresses.find(address => address.primary) ?? addresses[0];
}

export function primaryOrFirstAddressId(addresses: PatientAddress[]): number | null {
  return primaryOrFirstAddress(addresses)?.id ?? null;
}

export function addressHasCoordinates(
  addresses: PatientAddress[], addressId: number | null,
): boolean {
  // A null addressId (e.g. visits from plans created before this feature) resolves to the
  // patient's primary/default address, mirroring the backend's fallback.
  const address = addressId === null ?
    primaryOrFirstAddress(addresses) :
    addresses.find(candidate => candidate.id === addressId);

  return !!(address?.latitude && address?.longitude);
}

// A user who sees exactly one group has the group field hidden, so a plan missing its
// group must be pinned to that only group; any other case leaves the value untouched
// (never overwrite an existing group, never guess among several).
export function pinnedGroupId(
  groups: Array<{ id: number }> | undefined,
  currentGroupId: number | null,
): number | null {
  if (currentGroupId || !groups || groups.length !== 1) return null;

  return groups[0]!.id;
}
